import { useSearchParams } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { HealthState } from "contracts/enums/core";
import { createEffect, createMemo } from "solid-js";
import { matchesQuery } from "../../boundaries/text-search.search";
import type { PageMapLens } from "../../enums/map";
import { PAGE_MAP_LENS } from "../../enums/map";
import type { PageSortKey } from "../../enums/scan";
import { PAGE_SORT_KEY } from "../../enums/scan";
import { singleParam } from "../../ui/searchParam";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import {
  captureWorkspaceAnchor,
  scrollInventoryToPage,
} from "./pageListScroll";
import { isPageSortKey, sortPages } from "./pageSort";
import { isStrategicPage } from "./scanExperienceModel";
import {
  clusterContextForPages,
  clusterKey,
  estateClusters,
  pageContentFitnessScore,
  pageHealthClass,
} from "./scanModel";

// "nodata" pages have nothing to filter on, so the score filter skips it.
export type ScoreFilter = "all" | Exclude<HealthState, "nodata">;
type Page = OverviewResponse["pages"][number];

export function usePagesView(data: () => OverviewResponse) {
  const [search, setSearch] = useSearchParams();
  const clusters = createMemo(() => estateClusters(data()));
  const clusterContext = createMemo(() => clusterContextForPages(data().pages));
  const query = createMemo(() => singleParam(search.q) ?? "");
  const setQuery = (value: string) => {
    setSearch({ q: value || undefined });
  };
  const scoreFilter = createMemo<ScoreFilter>(() => {
    const value = singleParam(search.score);
    return isScoreFilter(value) ? value : "all";
  });
  const setScoreFilter = (value: ScoreFilter) => {
    setSearch({ score: value === "all" ? undefined : value });
  };
  const cluster = createMemo(() => {
    const value = singleParam(search.cluster);
    return value && clusters().some((item) => item.key === value)
      ? value
      : "all";
  });
  const setCluster = (value: string) => {
    setSearch({ cluster: value === "all" ? undefined : value });
  };
  const highValueOnly = createMemo(() => singleParam(search.priority) === "1");
  const setHighValueOnly = (value: boolean) => {
    setSearch({ priority: value ? "1" : undefined });
  };
  const lens = createMemo<PageMapLens>(() =>
    singleParam(search.lens) === "agent"
      ? PAGE_MAP_LENS.AGENT
      : PAGE_MAP_LENS.HUMAN,
  );
  const sortKey = createMemo(() => {
    const value = singleParam(search.sort);
    return isPageSortKey(value) ? value : PAGE_SORT_KEY.PRIORITY;
  });
  const setLens = (nextLens: PageMapLens) => {
    setSearch({
      lens: nextLens === PAGE_MAP_LENS.HUMAN ? undefined : nextLens,
    });
  };
  const setSortKey = (nextSort: PageSortKey) => {
    setSearch({ sort: nextSort });
  };
  createEffect(() => {
    if (!isPageSortKey(singleParam(search.sort))) {
      setSearch({ sort: "priority" }, { replace: true });
    }
  });
  const selectedPageId = createMemo(() => singleParam(search.page));
  const activeScore = (page: Page) =>
    lens() === PAGE_MAP_LENS.AGENT
      ? page.agentScore
      : pageContentFitnessScore(page);
  const pageMatchesQuery = (page: Page) =>
    matchesQuery(
      query(),
      pageDisplayLabel(page, data().site),
      rawPagePath(page),
    );
  const matchesCluster = (page: Page) =>
    cluster() === "all" ||
    clusterKey(page.path, clusterContext()) === cluster();
  const matchesScore = (page: Page) =>
    scoreFilter() === "all" ||
    pageHealthClass(activeScore(page)) === scoreFilter();
  const matchesValue = (page: Page) =>
    !highValueOnly() || isStrategicPage(data(), page);
  const matchesFilters = (page: Page) =>
    pageMatchesQuery(page) &&
    matchesCluster(page) &&
    matchesScore(page) &&
    matchesValue(page);
  const filteredPages = createMemo(() => data().pages.filter(matchesFilters));
  const visiblePages = createMemo(() =>
    sortPages(filteredPages(), sortKey(), data(), lens()),
  );
  const selectedPage = createMemo(
    () => data().pages.find((page) => page.id === selectedPageId()) ?? null,
  );
  const selectedPageHidden = createMemo(() => {
    const page = selectedPage();
    return Boolean(
      page && !visiblePages().some((visiblePage) => visiblePage.id === page.id),
    );
  });
  const selectedCluster = () => (cluster() === "all" ? null : cluster());
  const revealSelectedPage = () => {
    const page = selectedPage();
    if (!page) {
      return;
    }
    const workspaceAnchor = captureWorkspaceAnchor();
    setSearch({
      q: pageMatchesQuery(page)
        ? (singleParam(search.q) ?? undefined)
        : undefined,
      cluster: matchesCluster(page)
        ? (singleParam(search.cluster) ?? undefined)
        : undefined,
      score: matchesScore(page)
        ? (singleParam(search.score) ?? undefined)
        : undefined,
      priority: matchesValue(page)
        ? (singleParam(search.priority) ?? undefined)
        : undefined,
    });
    scrollInventoryToPage(page.id, workspaceAnchor);
  };
  const selectPage = (pageId: string) => {
    setSearch({ page: pageId });
  };
  const clearSelection = () => {
    setSearch({ page: undefined });
  };
  return {
    query,
    setQuery,
    scoreFilter,
    setScoreFilter,
    cluster,
    setCluster,
    highValueOnly,
    setHighValueOnly,
    lens,
    setLens,
    sortKey,
    setSortKey,
    selectedPageId,
    clusters,
    activeScore,
    visiblePages,
    selectedPage,
    selectedPageHidden,
    selectedCluster,
    selectPage,
    clearSelection,
    revealSelectedPage,
  };
}

function isScoreFilter(value: string | null): value is ScoreFilter {
  return value === "poor" || value === "watch" || value === "good";
}
